import type { ContractsBulkResponse } from './types';

import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodContractsBulkResponseSchema } from './validations';

/**
 * Fetches multiple contracts by IDs in bulk from the API.
 *
 * @param contractIds - The UUIDs of the contracts to fetch.
 * @param options - Additional request options.
 * @returns The paginated contracts bulk response.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contracts_by_ids_contracts_query_ids_post
 */
export const fetchContractsBulk = async (
  contractIds: UUID[],
  options?: KyRequestOptions,
): Promise<ContractsBulkResponse | undefined> => {
  const response = await vestigasApi
    .post(ENDPOINT.CONTRACT.GET_BULK(), {
      json: { contractIds },
      ...options,
    })
    .json<ContractsBulkResponse | undefined>();

  const validatedResponse = zodContractsBulkResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Contracts bulk API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
